import React, { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'next-i18next'
import { AccessDenied } from 'shared/errors/access-denied'
import { BadRequest } from 'shared/errors/bad-request'
import { UnauthorizedResponse } from 'shared/errors/unauthorized-response'
import useUser, { isAuthorizedUser } from 'shared/hooks/use-user'
import ProfileLayout from '../../components/profile-layout'
import { updateUserSettings } from '../api/user-settings-api'
import { useUserSettings } from '../hooks/use-user-settings'
import {
  EditableUserDomainAccountSettings,
  UpdateUserDomainAccountSettingsPayload,
  UserDomainAccountSettingsErrorsInterface,
  UserSettingsErrorResponseContentInterface,
} from '../types/user-settings-interface'
import UserDomainAccountFields from './user-domain-account-fields'
import UserSettingsSkeleton from './user-settings-skeleton'

const defaultValues: EditableUserDomainAccountSettings = {
  displayName: null,
  profileImage: null,
  bio: null,
}

const defaultErrors = {
  displayName: '',
  profileImage: '',
  bio: '',
}

function UserDomainAccountSettings() {
  const { t } = useTranslation()
  const { user, mutate: mutateUserData } = useUser()
  const { data: userSettings, error, mutate: mutateUserSettings } = useUserSettings()
  const [tempState, setTempState] = useState<EditableUserDomainAccountSettings>(defaultValues)
  const [errors, setErrors] = useState<UserDomainAccountSettingsErrorsInterface>(defaultErrors)
  const [isFetching, setIsFetching] = useState(false)

  useEffect(() => {
    if (error instanceof UnauthorizedResponse) {
      window.location.replace(error.location)
      return
    } else if (error instanceof AccessDenied) {
      window.location.replace('/403')
    }
  }, [error])

  useEffect(() => {
    if (userSettings?.user) {
      setTempState({
        ...userSettings.user,
        displayName: userSettings.userDomainAccount.displayName,
        profileImage: userSettings.userDomainAccount.profileImage,
        bio: userSettings.userDomainAccount.bio,
      })
    }
  }, [userSettings])

  const updateProfile = async () => {
    try {
      setIsFetching(true)
      const data: UpdateUserDomainAccountSettingsPayload = {
        userDomainAccount: {
          displayName: tempState.displayName,
          profileImage: tempState.profileImage?.id || null,
          bio: tempState.bio,
        },
      }

      const response = await updateUserSettings(data)
      await mutateUserSettings(response.data, false)
      await mutateUserData(
        data =>
          data && {
            ...data,
            avatarUrl: response.data.userDomainAccount.profileImage.path,
          },
        false,
      )
      setIsFetching(false)
      toast.success(t('global.changes_saved'))
    } catch (e) {
      setIsFetching(false)
      if (e instanceof BadRequest) {
        const userSettingErrors = e.errors as unknown as UserSettingsErrorResponseContentInterface

        setErrors({
          displayName: userSettingErrors.fields?.userDomainAccount?.displayName?.join(''),
        })
      }
    }
  }

  return (
    <ProfileLayout
      title={t('profile.user_settings.profile.title')}
      isFetching={isFetching}
      isSaveButton
      onSave={updateProfile}
    >
      {userSettings && isAuthorizedUser(user) ? (
        <div className="flex h-fit w-full flex-col gap-2 overflow-x-hidden rounded-md bg-background-secondary md:gap-5">
          <div className="bg-brand-primary-subtle p-5">
            <span className="text-base font-bold text-text-primary">{`${t('global.email_address')}: ${
              userSettings.user.email
            }`}</span>
          </div>
          <div className="flex flex-col gap-7 px-5 pb-7 pt-5 md:gap-10 md:pb-10">
            <UserDomainAccountFields
              setProfileImage={profileImage => setTempState(prev => ({ ...prev, profileImage }))}
              setDisplayName={displayName => setTempState(prev => ({ ...prev, displayName }))}
              setDisplayBio={bio => setTempState(prev => ({ ...prev, bio }))}
              email={userSettings.user.email}
              errors={errors}
              displayName={tempState.displayName}
              bio={tempState.bio}
              profileImage={tempState.profileImage?.path || user.avatarUrl}
            />
          </div>
        </div>
      ) : (
        <UserSettingsSkeleton />
      )}
    </ProfileLayout>
  )
}

export default UserDomainAccountSettings
